import { CaretDownIcon } from "@phosphor-icons/react";
import { FormikContext, useFormik } from "formik";
import { useState } from "react";
import { useTranslations } from "use-intl";
import { object } from "yup";
import dayjs from "dayjs";
import CheckInSelection from "../hotels/CheckInSelection";
import VisitorsSelection from "../hotels/VisitorsSelections";
import { SwipeableDrawer } from "../SwipeableDrawer";
import {
  getDatesValidationSchema,
  getRoomsValidationSchema,
} from "../hotels/validation";
import { TextField } from "@mui/material";
import type { TRoom } from "@/app/lib/types/types";

enum DrawerComponent {
  CheckInSelection = "CheckInSelection",
  VisitorsSelection = "VisitorsSelection",
}

type HotelSearchFormValues = {
  dates: {
    checkIn: Date;
    checkOut: Date;
  };
  rooms: TRoom[];
};

interface Props {
  initialValues: HotelSearchFormValues;
  onSubmit: (values: HotelSearchFormValues) => void;
}

export const HotelSearchForm = ({ initialValues, onSubmit }: Props) => {
  const t = useTranslations();
  const [drawerComponent, setDrawerComponent] =
    useState<DrawerComponent | null>(null);

  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: object().shape({
      dates: getDatesValidationSchema(t),
      rooms: getRoomsValidationSchema(t),
    }),
    initialValues,
    onSubmit: (values) => {
      if (!formik.dirty) return;
      onSubmit(values);
    },
  });

  const handleDrawerClose = () => {
    setDrawerComponent(null);

    formik.submitForm();
  };

  const handleInputClick = (
    e: React.MouseEvent<HTMLDivElement>,
    component: DrawerComponent,
  ) => {
    e.preventDefault();
    setDrawerComponent(component);
  };

  const checkInValue = dayjs(formik.values.dates.checkIn).format("D MMM");
  const checkOutValue = formik.values.dates.checkOut
    ? dayjs(formik.values.dates.checkOut).format("D MMM")
    : "_";

  return (
    <FormikContext.Provider value={formik}>
      <div className="flex gap-2">
        <TextField
          name="dates"
          onMouseDown={(e) => {
            handleInputClick(e, DrawerComponent.CheckInSelection);
          }}
          fullWidth
          label={t("hotels.search.checkInAndCheckOut")}
          value={`${checkInValue} - ${checkOutValue}`}
        />
        <TextField
          fullWidth
          name="visitors"
          label={t("hotels.search.visitors")}
          value={t("hotels.search.visitorsSumWithTypes", {
            adultsCount: formik.values.rooms.reduce(
              (acc, room) => acc + room.adults,
              0,
            ),
            childrenCount: formik.values.rooms.reduce(
              (acc, room) => acc + room.children,
              0,
            ),
          })}
          slotProps={{
            input: {
              endAdornment: (
                <CaretDownIcon
                  className="shrink-0"
                  color="black"
                  size={20}
                  weight="bold"
                />
              ),
            },
          }}
          sx={{
            input: {
              overflow: "hidden",
              textOverflow: "ellipsis",
            },
          }}
          onMouseDown={(e) => {
            handleInputClick(e, DrawerComponent.VisitorsSelection);
          }}
        />
      </div>

      <SwipeableDrawer
        onClose={() => setDrawerComponent(null)}
        open={!!drawerComponent}
        hasFullHeight={drawerComponent === DrawerComponent.CheckInSelection}
      >
        {drawerComponent === DrawerComponent.CheckInSelection ? (
          <CheckInSelection
            formProps={formik}
            onClose={() => handleDrawerClose()}
          />
        ) : null}
        {drawerComponent === DrawerComponent.VisitorsSelection ? (
          <VisitorsSelection
            formProps={formik}
            onClose={() => handleDrawerClose()}
          />
        ) : null}
      </SwipeableDrawer>
    </FormikContext.Provider>
  );
};
